/**
 * Chapter 1 «Слова»: the lesson's key words one card at a time — hear it, see the pinyin
 * and meaning, then the sentence from the text it comes from. All of them become review
 * cards when the learner gets through the deck.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Lesson, Word } from '../../core/content.ts';
import { findSentence } from '../../core/content.ts';
import { useAppData } from '../../state/app-data.tsx';
import { Icon } from '../../ui/Icon.tsx';
import { canSpeak, speak } from '../../ui/speech.ts';
import { wordToCard } from './word-cards.ts';

/** The example sentence split around the word, so it can be highlighted. */
function exampleParts(lesson: Lesson, word: Word): { before: string; hit: string; after: string; ru: string } | null {
  const sentence = word.exampleSentenceId ? findSentence(lesson, word.exampleSentenceId) : undefined;
  if (!sentence) return null;
  const at = sentence.text.toLowerCase().indexOf(word.word.toLowerCase());
  if (at === -1) return { before: sentence.text, hit: '', after: '', ru: sentence.ru };
  return {
    before: sentence.text.slice(0, at),
    hit: sentence.text.slice(at, at + word.word.length),
    after: sentence.text.slice(at + word.word.length),
    ru: sentence.ru,
  };
}

export function LearnStep({
  lesson,
  wordIds,
  speechLang,
  onDone,
}: {
  lesson: Lesson;
  wordIds: readonly string[];
  speechLang: string;
  onDone: () => void;
}) {
  const { addCards } = useAppData();
  const words = useMemo(
    () => wordIds.map((id) => lesson.words.find((w) => w.id === id)).filter((w): w is Word => w !== undefined),
    [lesson.words, wordIds],
  );
  const [index, setIndex] = useState(0);
  const word = words[index];

  useEffect(() => {
    if (word) speak(word.word, speechLang);
  }, [word, speechLang]);

  if (!word) {
    return (
      <div className="lesson__footer">
        <button type="button" className="btn btn--primary btn--block" onClick={onDone}>
          Продолжить
        </button>
      </div>
    );
  }

  const example = exampleParts(lesson, word);
  const last = index === words.length - 1;
  const next = () => {
    if (!last) {
      setIndex(index + 1);
      return;
    }
    addCards(words.map((w) => wordToCard(lesson, w)));
    onDone();
  };

  return (
    <div className="learn">
      <p className="t-caps">Новые слова урока</p>
      <div className="learn__dots" aria-label={`Слово ${index + 1} из ${words.length}`}>
        {words.map((w, i) => (
          <span key={w.id} className={`learn__dot${i < index ? ' learn__dot--done' : i === index ? ' learn__dot--now' : ''}`} />
        ))}
      </div>

      <article className="learn__card" key={word.id} aria-live="polite">
        <div className="learn__head">
          <span className="learn__word han" lang={speechLang}>
            {word.word}
          </span>
          {canSpeak() && (
            <button
              type="button"
              className="sound-btn"
              onClick={() => speak(word.word, speechLang)}
              aria-label={`Произнести ${word.word}`}
            >
              <Icon name="speaker" size={24} />
            </button>
          )}
        </div>
        {(word.pinyin || word.ipa) && <p className="learn__pinyin">{word.pinyin ?? word.ipa}</p>}
        <p className="learn__ru">
          {word.ru}
          {word.pos && <span className="chip learn__pos">{word.pos}</span>}
        </p>
        {example && (
          <div className="learn__example">
            <p className="han" lang={speechLang}>
              {example.before}
              {example.hit && <mark>{example.hit}</mark>}
              {example.after}
            </p>
            <p className="t-meta">{example.ru}</p>
          </div>
        )}
      </article>

      <div className="lesson__footer learn__footer">
        <button
          type="button"
          className="btn btn--secondary"
          disabled={index === 0}
          onClick={() => setIndex(index - 1)}
          aria-label="Предыдущее слово"
        >
          <Icon name="back" size={22} />
        </button>
        <button type="button" className="btn btn--primary learn__next" onClick={next}>
          {last ? 'Все слова — в повторение' : 'Дальше'}
        </button>
      </div>
    </div>
  );
}
